/**
 * Client-side API guardrails (not a substitute for server auth).
 * Paths are relative to VITE_KEYWORDYAB_API_PREFIX (e.g. /api/keywords/...).
 */

const DEFAULT_ALLOWED_PATHS = [
  '/keywords/suggest',
  '/keywords/stats',
  '/keywords/jobs/:jobId',
  '/keywords/jobs/:jobId/keywords',
  '/browser-worker/poll',
  '/browser-worker/report-error',
  '/browser-worker/tasks/:taskId/heartbeat',
  '/browser-worker/tasks/:taskId/keywords',
  '/browser-worker/tasks/:taskId/progress',
  '/browser-worker/tasks/:taskId/complete',
  '/browser-worker/tasks/:taskId/fail',
] as const;

function parseCsv(raw: string | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function patternToRegex(pattern: string): RegExp {
  const segments = pattern.split('/').map((segment) => {
    if (segment.startsWith(':')) {
      return '[^/]+';
    }
    return segment.replace(/[.*+?^$(){}|[\]\\]/g, '\\$&');
  });
  return new RegExp('^' + segments.join('/') + '$');
}

const allowedOrigins = parseCsv(import.meta.env.VITE_KEYWORDYAB_ALLOWED_ORIGINS);
const allowedPathPatterns = (() => {
  const fromEnv = parseCsv(import.meta.env.VITE_KEYWORDYAB_ALLOWED_API_PATHS);
  return fromEnv.length > 0 ? fromEnv : [...DEFAULT_ALLOWED_PATHS];
})();

export function assertBackendApiAllowed(apiPath: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (allowedOrigins.length > 0) {
    const origin = window.location.origin;
    if (!allowedOrigins.includes(origin)) {
      throw new Error(
        'KeywordYab API blocked: origin ' +
          origin +
          ' is not in VITE_KEYWORDYAB_ALLOWED_ORIGINS',
      );
    }
  }

  const pathOnly = apiPath.split('?')[0].split('#')[0];
  const normalized = pathOnly.startsWith('/') ? pathOnly : '/' + pathOnly;

  const allowed = allowedPathPatterns.some((pattern) =>
    patternToRegex(pattern).test(normalized),
  );

  if (!allowed) {
    throw new Error(
      'KeywordYab API blocked: path ' +
        normalized +
        ' is not in VITE_KEYWORDYAB_ALLOWED_API_PATHS',
    );
  }
}

export function getConfiguredAllowedApiPaths(): readonly string[] {
  return allowedPathPatterns;
}

export function getConfiguredAllowedOrigins(): readonly string[] {
  return allowedOrigins;
}
